import { NavLink, Outlet } from "react-router-dom";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LayoutDashboard, PackagePlus, PackageMinus, Boxes, Users, Truck, LogOut, ShieldCheck, Settings as SettingsIcon, BarChart3, FileText, ClipboardList, Warehouse, ArrowRightLeft, ExternalLink, Factory } from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const LOGO_URL = "https://customer-assets-gfyr7b9c.emergentagent.net/job_battery-stock-hub/artifacts/1cicyv9h_GREENRGY%20NEW%20FINAL%20LOGO.webp";

const nav = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, tid: "nav-dashboard" },
  { to: "/inward", label: "Stock Inward", icon: PackagePlus, tid: "nav-inward" },
  { to: "/outward", label: "Sales / Dispatch", icon: PackageMinus, tid: "nav-outward" },
  { to: "/stock", label: "Stock In Hand", icon: Boxes, tid: "nav-stock" },
  { to: "/transfers", label: "Stock Transfers", icon: ArrowRightLeft, tid: "nav-transfers" },
  { to: "/production", label: "Production", icon: Factory, tid: "nav-production" },
  { to: "/vendors", label: "Vendors", icon: Truck, tid: "nav-vendors" },
  { to: "/customers", label: "Customers / B2B", icon: Users, tid: "nav-customers" },
  { to: "/purchase-orders", label: "Purchase Orders", icon: FileText, tid: "nav-po" },
  { to: "/sales-orders", label: "Sales Orders", icon: ClipboardList, tid: "nav-so" },
  { to: "/reports", label: "Reports", icon: BarChart3, tid: "nav-reports" },
];

const adminNav = [
  { to: "/locations", label: "Locations", icon: Warehouse, tid: "nav-locations" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, tid: "nav-settings" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const [customLink, setCustomLink] = useState({ label: "", url: "" });

  useEffect(() => {
    api.get("/settings/custom-link")
      .then((r) => setCustomLink(r.data || { label: "", url: "" }))
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen flex bg-[#F4F7F5] text-zinc-900">
      <aside className="w-60 flex flex-col shrink-0 bg-[#0B3022]" data-testid="sidebar">
        <div className="px-4 py-5 bg-white flex items-center justify-center border-b border-[#0B3022]/10">
          <img src={LOGO_URL} alt="GREENRGY POWER" className="h-11 w-auto" />
        </div>

        <nav className="flex-1 py-4 px-2">
          {nav.map((n) => {
            const Icon = n.icon;
            return (
              <NavLink key={n.to} to={n.to} data-testid={n.tid} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                <Icon className="w-4 h-4" />
                {n.label}
              </NavLink>
            );
          })}
          {user?.role === "admin" && (
            <>
              <div className="mt-4 mb-1 px-3 text-[10px] font-bold uppercase tracking-widest text-[#8EEA20]/70">Admin</div>
              {adminNav.map((n) => {
                const Icon = n.icon;
                return (
                  <NavLink key={n.to} to={n.to} data-testid={n.tid} className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}>
                    <Icon className="w-4 h-4" />
                    {n.label}
                  </NavLink>
                );
              })}
            </>
          )}
        </nav>

        <div className="border-t border-white/10 p-3">
          <div className="flex items-center gap-3 mb-3">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-9 h-9 rounded-full border border-white/20" />
            ) : (
              <div className="w-9 h-9 bg-white/10 rounded-full" />
            )}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-semibold text-white truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-[10px] text-[#DFF7E3]/70 truncate flex items-center gap-1">
                {user?.role === "admin" && <ShieldCheck className="w-3 h-3 text-[#8EEA20]" />}
                <span className="uppercase tracking-widest" data-testid="user-role">{user?.role}</span>
              </div>
            </div>
          </div>
          <button onClick={logout} data-testid="logout-btn" className="w-full flex items-center justify-center gap-2 text-xs font-semibold text-[#DFF7E3] bg-white/5 hover:bg-white/10 rounded-lg py-2 transition-colors">
            <LogOut className="w-3.5 h-3.5" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-x-hidden">
        {customLink.label && customLink.url && (
          <div className="border-b border-zinc-200 bg-white px-8 py-3 flex justify-end" data-testid="topbar">
            <a
              href={customLink.url}
              data-testid="custom-link-btn"
              className="inline-flex items-center gap-2 px-4 py-2 bg-[#0B3022] text-white text-xs uppercase font-bold tracking-widest hover:bg-[#16A34A] transition-colors rounded"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              {customLink.label}
            </a>
          </div>
        )}
        <Outlet />
        <Toaster theme="light" position="top-right" richColors />
      </main>
    </div>
  );
}
